import type { ColDef } from "ag-grid-community";

import {
  TimestampCell,
  timestampCellClass,
  type TimestampVariant,
} from "./Cells/TimestampCell";

/** The instant-like value a timestamp cell can render. */
type TimestampValue = string | number | Date;

interface ColumnHelper<TData> {
  /**
   * Type-checked accessor by field name. The cell renderer's `value` is typed
   * as `TData[K]`.
   */
  accessor<K extends keyof TData & string>(
    field: K,
    opts?: Omit<ColDef<TData, TData[K]>, "field" | "valueGetter">,
  ): ColDef<TData, TData[K]>;

  /**
   * Column for a timestamp field, pre-wired with `TimestampCell` and
   * `timestampCellClass`. `variant` selects the display — `precise` (default)
   * or the adaptive `relative`.
   *
   * The cell accepts an ISO 8601 string or a `Date`. For any other wire format
   * (e.g. nanosecond `bigint` event times), pass a `valueGetter` that
   * normalizes the row into a `Date` — the caller owns parsing since it knows
   * the field's format.
   *
   * No comparator is set, so sorting is left to the grid/consumer: a
   * client-side table can pass one via `opts`, while `DataTable` (the current
   * consumer) sorts server-side and neutralizes client reordering with its own
   * default `comparator: () => 0`.
   */
  timestamp<K extends keyof TData & string>(
    field: K,
    // Opts are typed against the instant value the cell consumes
    // (`string | number | Date`), not `TData[K]` — so a `valueGetter` may
    // normalize any wire format (e.g. nanosecond `bigint`) into a `Date`.
    opts?: Omit<
      ColDef<TData, TimestampValue>,
      "field" | "cellRenderer" | "cellClass"
    > & { variant?: TimestampVariant },
  ): ColDef<TData, TData[K]>;
}

/**
 * Ergonomic factories for column shapes. Returns plain AG Grid `ColDef`s,
 * so callers can mix helper output freely with hand-written column defs
 * when they need features the helper doesn't shortcut.
 *
 * Inspired by Tanstack Table's createColumnHelper
 */
export function createColumnHelper<TData>(): ColumnHelper<TData> {
  return {
    accessor(field, opts) {
      // ColDef.field is typed as the recursive nested-path union ColDefField<TData, TValue>,
      // so a plain string key can't be assigned without going through unknown.
      return { field, ...opts } as unknown as ColDef<
        TData,
        TData[typeof field]
      >;
    },
    timestamp(field, opts) {
      const { variant = "precise", ...colDef } = opts ?? {};
      return {
        field,
        cellRenderer: TimestampCell,
        cellClass: timestampCellClass,
        cellRendererParams: { variant },
        ...colDef,
      } as unknown as ColDef<TData, TData[typeof field]>;
    },
  };
}
